/**
 * Setting a new password from a recovery link — `/admin/reset-password`.
 *
 * Supabase's recovery mail now lands on the production domain, and this is the screen that was
 * missing at the other end of it: the sign-in screen only ever accepted an EXISTING password, so a
 * valid link arrived somewhere that had nothing to do with it.
 *
 * Three things this screen is careful about.
 *
 * It is not a sign-in. The link establishes a session of its own inside the adapter — its own
 * client, its own in-memory storage — which may do exactly one thing, set a password, and is then
 * retired. Nothing here calls `signIn`, reads `auth`, or feeds `SessionProvider`'s state, so a
 * recovery can never become a console session and an ordinary signed-in visitor can never reach
 * the form: without a grant in the address there is no recovery session, and without a recovery
 * session there is no form. The second factor is untouched — the person signs in afterwards with
 * the new password and answers TOTP exactly as before.
 *
 * It fails closed. An expired link, a spent link, a link the provider will not verify, a link of
 * the wrong type, and an address with nothing in it all end in the same place: one Turkish
 * sentence and a way back to the sign-in screen. The form is not rendered in any of them, and the
 * provider's own words are never shown — `recoveryMessage` maps a code, it never echoes.
 *
 * It leaves nothing behind. The address is scrubbed with `replaceState` BEFORE the provider is
 * asked anything, so the tokens are out of the bar, out of `document.location` and out of the back
 * stack even if the round trip hangs. The parsed grant is passed straight to the adapter and never
 * put in state; the two password fields are cleared on the way out, whether the attempt worked or
 * not.
 *
 * There is no «send me a link» control, deliberately. This screen completes a link an
 * administrator raised; it does not let anyone ask for one.
 */
import { type FormEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PASSWORD_RULE_TEXT, passwordProblem } from '../auth/password-policy';
import { isRecoveryGrant, parseRecoveryLink, type RecoveryLink } from '../auth/recovery-link';
import { useSession } from '../auth/session';
import { AUTH_MESSAGES, keepsRecoveryFormOpen, recoveryMessage } from '../auth/supabaseAdapter';
import { Box, Button, Loading, TextField } from '../ui';
import { BrandMark } from '../ui/Icon';
import { Mosaic } from '../ui/Mosaic';

/** `checking` while the link is being verified; then either the form or a refusal, never both. */
type Stage = 'checking' | 'form' | 'refused';

/**
 * Takes the recovery parameters out of the address.
 *
 * `replaceState` rather than a navigation: the entry is REPLACED, so the address with the tokens
 * in it is not left in the back stack for someone to return to. The path is kept and everything
 * after it is dropped, which is both halves — the query and the fragment — in one go.
 */
function scrubAddress(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname);
  } catch {
    /* A browser that refuses the call is not a reason to stop: the link is consumed regardless,
       and it is single-use at the provider. */
  }
}

export function ResetPasswordScreen(): ReactElement {
  const { supportsRecovery, beginPasswordRecovery, completePasswordRecovery, endPasswordRecovery } =
    useSession();
  const navigate = useNavigate();

  /*
   * Read once, before anything else can change the address. A lazy initializer runs during render,
   * which is the only moment the parameters are still there — the effect below scrubs them.
   */
  const [link] = useState<RecoveryLink>(() =>
    parseRecoveryLink({ search: window.location.search, hash: window.location.hash }),
  );

  const [stage, setStage] = useState<Stage>('checking');
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Exactly one consume, whatever React does with the effect.
   *
   * StrictMode runs an effect twice on the same instance in development, and this is the effect
   * that spends a single-use link: a second `exchangeCodeForSession`, `verifyOtp` or `setSession`
   * would be answered by the provider with «already used» and would refuse a link that was
   * perfectly good. A ref survives the double invocation; `link` was captured before the scrub, so
   * the second pass has nothing left to read anyway.
   */
  const consumed = useRef(false);

  /*
   * «Is this screen still on the page», as a ref rather than as a closure.
   *
   * The obvious spelling — a `let alive` captured per effect run — is wrong precisely BECAUSE the
   * consume is guarded: StrictMode's cleanup would set the first run's `alive` to false, the
   * second run would find the guard already spent and start nothing, and the answer to the one
   * call that was made would then be dropped by a flag nobody reset. The form would simply never
   * open. A ref is re-armed by every run and only lowered for good on a real unmount.
   */
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    if (!consumed.current) {
      consumed.current = true;

      /* Out of the address first — before the network, not after it. */
      if (isRecoveryGrant(link)) scrubAddress();

      if (!supportsRecovery) {
        setError(AUTH_MESSAGES.recovery_not_supported ?? null);
        setStage('refused');
      } else if (!isRecoveryGrant(link)) {
        /* `refused` (the provider already said no) and `absent` (nothing to consume, which is
           where an ordinary session or a typed address lands) are one outcome to the reader. */
        setError(AUTH_MESSAGES.recovery_link_invalid ?? null);
        setStage('refused');
      } else {
        void (async () => {
          try {
            const session = await beginPasswordRecovery(link);
            if (!live.current) return;
            setAccountEmail(session.email);
            setStage('form');
          } catch (cause) {
            if (!live.current) return;
            setError(recoveryMessage(cause));
            setStage('refused');
          }
        })();
      }
    }
    return () => {
      live.current = false;
    };
  }, [link, supportsRecovery, beginPasswordRecovery]);

  /* One submit at a time; `busy` disables the button and this closes the gap before it renders. */
  const submitting = useRef(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submitting.current) return;
    setError(null);

    /*
     * The rule is checked here, so a pair that cannot be accepted never becomes a provider call:
     * a mismatch is the user's own typing and there is nothing to ask the server about.
     *
     * This is also the one refusal that does NOT empty the fields, deliberately. Nothing has left
     * the browser, so there is nothing to clear for; emptying them would make a typo in the second
     * box cost the person the whole passphrase they just chose.
     */
    const problem = passwordProblem(password, confirmation);
    if (problem) {
      setError(problem);
      return;
    }

    submitting.current = true;
    setBusy(true);
    try {
      await completePasswordRecovery(password);
      /* Out of state before anything else happens, including the navigation. */
      setPassword('');
      setConfirmation('');
      await endPasswordRecovery();
      /*
       * `replace`, and a flag rather than a sentence: the sign-in screen owns its own words, so
       * nothing rendered there comes out of the history entry.
       */
      navigate('/admin/login', { replace: true, state: { passwordJustSet: true } });
    } catch (cause) {
      setPassword('');
      setConfirmation('');
      setError(recoveryMessage(cause));
      /* Only a refusal about the PASSWORD keeps the form; anything about the link closes it. */
      if (!keepsRecoveryFormOpen(cause)) setStage('refused');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="login-wrap">
      <Mosaic cols={48} rows={3} className="login-mosaic" />
      <div className="login-card">
        <span className="brand" style={{ padding: 0 }}>
          <BrandMark />
          <span className="txt">
            <b>HPW Credentials</b>
            <span className="sub">Happy Place to Work</span>
          </span>
        </span>

        {stage === 'checking' ? <Loading label="Bağlantı doğrulanıyor…" /> : null}

        {stage === 'refused' ? (
          <>
            <div>
              <h1>Parola yenilenemedi</h1>
              <p className="muted" style={{ marginTop: 6 }}>
                Bağlantılar tek kullanımlıktır ve kısa süre sonra geçerliliğini yitirir.
              </p>
            </div>
            {error ? <Box tone="warn">{error}</Box> : null}
            <p className="help">
              <Link to="/admin/login" onClick={() => void endPasswordRecovery()}>
                Giriş ekranına dönün
              </Link>
              .
            </p>
          </>
        ) : null}

        {stage === 'form' ? (
          <>
            <div>
              <h1>Yeni parola belirleyin</h1>
              <p className="muted" style={{ marginTop: 6 }}>
                {accountEmail
                  ? `${accountEmail} hesabı için yeni bir parola belirleyin.`
                  : 'Hesabınız için yeni bir parola belirleyin.'}{' '}
                Parolanızı kaydettikten sonra giriş ekranında yeni parolanızla ve iki adımlı doğrulama
                kodunuzla giriş yapacaksınız.
              </p>
            </div>
            <form className="stack" style={{ gap: 14 }} onSubmit={(event) => void submit(event)}>
              <TextField
                label="Yeni parola"
                type="password"
                autoComplete="new-password"
                help={PASSWORD_RULE_TEXT}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <TextField
                label="Yeni parolayı doğrulayın"
                type="password"
                autoComplete="new-password"
                help="Aynı parolayı bir kez daha yazın."
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
              />
              {error ? <Box tone="warn">{error}</Box> : null}
              <Button type="submit" variant="primary" size="lg" block busy={busy}>
                Parolayı kaydet
              </Button>
            </form>
          </>
        ) : null}
      </div>
    </main>
  );
}
